import { createContext, useContext, useState, useCallback, useEffect, useRef } from "react";
import { api } from "../lib/api";

const CustomerViewContext = createContext(null);
const STORAGE_KEY = "zelligio-display-mode";

export function CustomerViewProvider({ children }) {
  // §27.11: Customer Mode persists across navigation/reload (localStorage) and is
  // mirrored server-side (POST /dealer/display-mode) so the privacy boundary never
  // depends on a client-supplied flag alone.
  const [customerView, setCustomerView] = useState(() => localStorage.getItem(STORAGE_KEY) === "customer");
  const modeSyncRef = useRef(null);
  const toggleCustomerView = useCallback(() => {
    setCustomerView((v) => {
      const next = !v;
      localStorage.setItem(STORAGE_KEY, next ? "customer" : "trade");
      modeSyncRef.current = api.post("/auth/dealer/display-mode", { customer_view: next }).catch(() => {});
      return next;
    });
  }, []);
  // Copilot sends await this so a mode toggle can never race a request made with the
  // previous disclosure mode server-side (E2E finding — iteration_61).
  const awaitModeSync = useCallback(() => modeSyncRef.current || Promise.resolve(), []);
  // Adopt the server-persisted preference on load (another device may have set it).
  // §13 MODE-050 fail closed: a stale server flag may downgrade to Customer, but never
  // silently upgrades a client showing Customer to Trade (that would expose
  // dealer-private pricing under a customer-safe presentation) — re-mirror instead.
  useEffect(() => {
    api.get("/auth/me").then((r) => {
      const server = Boolean(r.data?.display_customer_view);
      setCustomerView((v) => {
        if (v && !server) {
          api.post("/auth/dealer/display-mode", { customer_view: true }).catch(() => {});
          return v;
        }
        if (!v && server) localStorage.setItem(STORAGE_KEY, "customer");
        return v || server;
      });
    }).catch(() => {});
  }, []);
  return (
    <CustomerViewContext.Provider value={{ customerView, toggleCustomerView, awaitModeSync }}>
      {children}
    </CustomerViewContext.Provider>
  );
}

export function useCustomerView() {
  return useContext(CustomerViewContext);
}
